import { sha256 } from './utils';

/**
 * Generates a unique, stable browser fingerprint for tracking
 * Uses multiple browser characteristics to create a highly unique identifier
 * GDPR compliant - no PII is collected
 */
export async function generateFingerprint(): Promise<string> {
  const components = [
    // User agent and language (high entropy)
    navigator.userAgent,
    navigator.language,
    (navigator.languages || []).join(','),

    // Screen (very high entropy, cheap)
    screen.width,
    screen.height,
    screen.colorDepth,
    window.devicePixelRatio || 1,

    // Hardware
    navigator.hardwareConcurrency || 0,
    (navigator as any).deviceMemory || 0,
    navigator.maxTouchPoints || 0,

    // Browser/OS
    navigator.platform,
    new Date().getTimezoneOffset(),

    // WebGL (high entropy)
    getWebGLFingerprint(),

    // Canvas (high entropy, optimized)
    await getCanvasFingerprint(),
  ];

  return sha256(components.join('~'));
}

/**
 * WebGL fingerprinting - vendor and renderer info
 */
function getWebGLFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return '0';

    const debugInfo = (gl as any).getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return '1';

    const vendor = (gl as any).getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    const renderer = (gl as any).getParameter(
      debugInfo.UNMASKED_RENDERER_WEBGL,
    );

    return vendor + '~' + renderer;
  } catch {
    return '2';
  }
}

/**
 * Canvas fingerprinting - optimized for size
 */
async function getCanvasFingerprint(): Promise<string> {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 20;

    const ctx = canvas.getContext('2d');
    if (!ctx) return '0';

    // Use non-existent font to force system fallback (high entropy)
    ctx.textBaseline = 'top';
    ctx.font = "14px 'PulsoraFont123'";
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';

    // Text with emoji for extra entropy
    ctx.fillText('Cwm fjord 🎨 glyph', 2, 15);

    // Extract just a portion of the data URL to save space
    const dataUrl = canvas.toDataURL();
    // Take middle portion for better entropy
    return dataUrl.substring(100, 200);
  } catch {
    return '1';
  }
}
